import React, { useMemo } from 'react'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import FormFields from 'app/settings/components/OpeningHours/Form'
import { Typography } from '@mui/material'
import { BasicFormLayout, Form } from '@carsys/form'
import { IWorkHours } from '@cbgms/api'
import * as Yup from 'yup'
import { updateOpeningHours } from 'app/settings/state/OpeningHours/actions'
import { useDispatch } from 'react-redux'
import { useNotifier } from '@cbgms/base/notifier'
import useGetOpeningHours from './use-get-opening-hours'
import moment from 'moment'
import { Loader } from '@carsys/ui'

const defaultWeekdayStartTime = ''
const defaultWeekdayEndTime = ''
const defaultWeekendStartTime = ''
const defaultWeekendEndTime = ''

export const initialValues: IWorkHours = {
  BreakTimes: { Times: [] },
  MondayActive: false,
  MondayStartTime: defaultWeekdayStartTime,
  MondayEndTime: defaultWeekdayEndTime,
  TuesdayActive: false,
  TuesdayStartTime: defaultWeekdayStartTime,
  TuesdayEndTime: defaultWeekdayEndTime,
  WednesdayActive: false,
  WednesdayStartTime: defaultWeekdayStartTime,
  WednesdayEndTime: defaultWeekdayEndTime,
  ThursdayActive: false,
  ThursdayStartTime: defaultWeekdayStartTime,
  ThursdayEndTime: defaultWeekdayEndTime,
  FridayActive: false,
  FridayStartTime: defaultWeekdayStartTime,
  FridayEndTime: defaultWeekdayEndTime,
  SaturdayActive: false,
  SaturdayStartTime: defaultWeekendStartTime,
  SaturdayEndTime: defaultWeekendEndTime,
  SundayActive: false,
  SundayStartTime: defaultWeekendStartTime,
  SundayEndTime: defaultWeekendEndTime,
  IsShopDefault: true
}

export const testTimeFormat = (t: TFunction<'translation'>) => {
  return {
    name: 'is-time-format',
    test: (value: string | undefined) => !!value && moment(value, 'HH:mm').isValid(),
    message: t('global.time_format_invalid')
  }
}
export const testTimeFormatOptional = (t: TFunction<'translation'>) => {
  return {
    name: 'is-time-format',
    test: (value: string | undefined) => !value || moment(value, 'HH:mm').isValid(),
    message: t('global.time_format_invalid')
  }
}
export const endAfterStartTime =
  (t: TFunction<'translation', undefined>) =>
  ([startTime]: any[], schema: Yup.Schema) => {
    return schema
      .required(`${t('global.field_is_required')}`)
      .test(testTimeFormat(t))
      .test('end-is-after-start', t('global.time_end_should_be_before_start'), (endTime: string) => {
        return moment(endTime, 'HH:mm').diff(moment(startTime, 'HH:mm')) > 0
      })
  }

export const endAfterStartTimeWithActive =
  (t: TFunction<'translation', undefined>) =>
  ([active, startTime]: any[], schema: Yup.Schema) => {
    if (!active) {
      return schema
    }
    return endAfterStartTime(t)([startTime], schema)
  }

export const buildValidationSchema = (t: TFunction<'translation', undefined>) => {
  return Yup.object().shape({
    BreakTimes: Yup.object().shape({
      Times: Yup.array().of(
        Yup.object().shape({
          BeginTime: Yup.string()
            .test(testTimeFormat(t))
            .required(`${t('global.field_is_required')}`),
          EndTime: Yup.string()
            .test(testTimeFormat(t))
            .when(['BeginTime'], endAfterStartTime(t))
            .required(`${t('global.field_is_required')}`)
        })
      )
    }),
    MondayStartTime: Yup.string().when('MondayActive', {
      is: true,
      then: schema => schema.test(testTimeFormat(t)).required(`${t('global.field_is_required')}`)
    }),
    MondayEndTime: Yup.string().when(['MondayActive', 'MondayStartTime'], endAfterStartTimeWithActive(t)),
    TuesdayStartTime: Yup.string().when('TuesdayActive', {
      is: true,
      then: schema => schema.test(testTimeFormat(t)).required(`${t('global.field_is_required')}`)
    }),
    TuesdayEndTime: Yup.string().when(['TuesdayActive', 'TuesdayStartTime'], endAfterStartTimeWithActive(t)),
    WednesdayStartTime: Yup.string().when('WednesdayActive', {
      is: true,
      then: schema => schema.test(testTimeFormat(t)).required(`${t('global.field_is_required')}`)
    }),
    WednesdayEndTime: Yup.string().when(['WednesdayActive', 'WednesdayStartTime'], endAfterStartTimeWithActive(t)),
    ThursdayStartTime: Yup.string().when('ThursdayActive', {
      is: true,
      then: schema => schema.test(testTimeFormat(t)).required(`${t('global.field_is_required')}`)
    }),
    ThursdayEndTime: Yup.string().when(['ThursdayActive', 'ThursdayStartTime'], endAfterStartTimeWithActive(t)),
    FridayStartTime: Yup.string().when('FridayActive', {
      is: true,
      then: schema => schema.test(testTimeFormat(t)).required(`${t('global.field_is_required')}`)
    }),
    FridayEndTime: Yup.string().when(['FridayActive', 'FridayStartTime'], endAfterStartTimeWithActive(t)),
    SaturdayStartTime: Yup.string().when('SaturdayActive', {
      is: true,
      then: schema => schema.test(testTimeFormat(t)).required(`${t('global.field_is_required')}`)
    }),
    SaturdayEndTime: Yup.string().when(['SaturdayActive', 'SaturdayStartTime'], endAfterStartTimeWithActive(t)),
    SundayStartTime: Yup.string().when('SundayActive', {
      is: true,
      then: schema => schema.test(testTimeFormat(t)).required(`${t('global.field_is_required')}`)
    }),
    SundayEndTime: Yup.string().when(['SundayActive', 'SundayStartTime'], endAfterStartTimeWithActive(t))
  })
}

export const OpeningHours: React.FC<IWorkHours> = props => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const notifier = useNotifier()
  const { data: openingHours, refetch, isLoading } = useGetOpeningHours()

  const validationSchema = useMemo(() => buildValidationSchema(t), [t])

  // Do not render until opening hours are loaded.  The form doesn't properly utilize enableReinitialize
  if (isLoading) return <Loader loading />

  return (
    <Form
      initialValues={openingHours || initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={async values => {
        await dispatch(updateOpeningHours(values))
        refetch()
        return values
      }}
      onSubmitSuccess={() => {
        notifier.success(t('setting_opening_hour.opening_hours_successfully_updated'))
      }}
    >
      <BasicFormLayout>
        <Typography data-testid='openinghours-title' variant='h6'>
          {t('setting_opening_hour.opening_hours')}
        </Typography>
        <Typography variant='body1' sx={{ mb: 1 }}>
          {t('setting_opening_hour.set_your_opening_hours')}
        </Typography>
        <FormFields {...props} />
      </BasicFormLayout>
    </Form>
  )
}

OpeningHours.displayName = 'OpeningHours'

export default OpeningHours
